import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import config from '../../config/environment';
import { DateDisplay } from '../../components/Forms';

interface LicenseData {
  commercialRegister: string;
  tourismLicense: string;
  transportationLicense: string;
  taxNumber: string;
}

const VendorSettings: React.FC = () => {
  const [licenseData, setLicenseData] = useState<LicenseData>({
    commercialRegister: '',
    tourismLicense: '',
    transportationLicense: '',
    taxNumber: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Helper function to get full logo URL
  const getLogoUrl = (logoPath: string | null | undefined) => {
    if (!logoPath) return null;
    // If it's already a full URL, return as is
    if (logoPath.startsWith('http')) return logoPath;
    // Otherwise, prepend the hajksa API base URL
    const fullUrl = `https://api.hajksa.com${logoPath}`;
    console.log('Logo URL generated:', { logoPath, fullUrl });
    return fullUrl;
  };

  // Enable API call with detailed debugging
  const { data: vendorData, isLoading, refetch, error: queryError } = useQuery({
    queryKey: ['vendor-profile'],
    queryFn: async () => {
      try {
        console.log('🔍 VendorSettings: Starting API call to /vendor/profile');
        console.log('🔍 VendorSettings: Current auth state:', {
          hasToken: !!localStorage.getItem('token'),
          hasUser: !!localStorage.getItem('user'),
          hasVendor: !!localStorage.getItem('currentVendor'),
          currentPath: window.location.pathname
        });
        
        const result = await apiService.getVendorProfile();
        console.log('✅ VendorSettings: API call successful:', result);
        return result;
      } catch (apiError: any) {
        console.error('❌ VendorSettings: API call failed:', {
          status: apiError.response?.status,
          message: apiError.response?.data?.message,
          error: apiError.message
        });
        
        // Don't let the error propagate if it's a 401 - handle it gracefully
        if (apiError.response?.status === 401) {
          console.log('🚨 VendorSettings: Got 401, but not throwing to prevent redirect');
          setError('جلسة المستخدم منتهية الصلاحية. يرجى تسجيل الدخول مرة أخرى.');
          return null; // Return null instead of throwing
        }
        
        throw apiError; // Re-throw other errors
      }
    },
    retry: 0, // Don't retry to avoid multiple auth failures
    enabled: true
  });

  // Update license data when vendor data is fetched
  useEffect(() => {
    if (vendorData?.license) {
      setLicenseData({
        commercialRegister: vendorData.license.commercialRegister || '',
        tourismLicense: vendorData.license.tourismLicense || '',
        transportationLicense: vendorData.license.transportationLicense || '',
        taxNumber: vendorData.license.taxNumber || ''
      });
    }
  }, [vendorData]);

  const updateLicenseMutation = useMutation({
    mutationFn: (data: LicenseData) => apiService.updateVendorLicense(data),
    onSuccess: () => {
      setSuccess('تم تحديث معلومات الترخيص بنجاح');
      setError('');
      setIsEditing(false);
      refetch();
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      console.error('License update error:', error);
      setError(error.response?.data?.message || 'حدث خطأ أثناء تحديث معلومات الترخيص');
      setSuccess('');
    }
  });

  const uploadLogoMutation = useMutation({
    mutationFn: (logoFile: File) => apiService.uploadVendorLogo(logoFile),
    onSuccess: () => {
      setSuccess('تم رفع الشعار بنجاح');
      setError('');
      setSelectedLogo(null);
      setLogoPreview(null);
      refetch();
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      console.error('Logo upload error:', error);
      setError(error.response?.data?.message || 'حدث خطأ أثناء رفع الشعار');
      setSuccess('');
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLicenseData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('يرجى اختيار ملف صورة صحيح');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('حجم الملف كبير جداً. الحد الأقصى 5 ميجابايت');
        return;
      }
      
      setSelectedLogo(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleLogoUpload = () => {
    if (selectedLogo) {
      uploadLogoMutation.mutate(selectedLogo);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate required field
    if (!licenseData.commercialRegister.trim()) {
      setError('رقم السجل التجاري مطلوب');
      return;
    }

    updateLicenseMutation.mutate(licenseData);
  };

  const handleCancel = () => {
    // Reset to original data
    if (vendorData?.license) {
      setLicenseData({
        commercialRegister: vendorData.license.commercialRegister || '',
        tourismLicense: vendorData.license.tourismLicense || '',
        transportationLicense: vendorData.license.transportationLicense || '',
        taxNumber: vendorData.license.taxNumber || ''
      });
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="mr-3 text-gray-600">جاري تحميل البيانات...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">إعدادات الترخيص</h1>
            <p className="text-gray-600 mt-1">إدارة معلومات الترخيص والسجلات التجارية</p>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="mr-3">
                <p className="text-sm font-medium text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="mr-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Logo Upload Card */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">شعار الشركة</h2>
            <p className="text-gray-600 text-sm mt-1">رفع وإدارة شعار الشركة</p>
          </div>
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Current Logo */}
              <div className="flex-shrink-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">الشعار الحالي</label>
                                                  <div className="w-32 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden relative">
                   {getLogoUrl(vendorData?.logo) ? (
                     <>
                       <img 
                         src={getLogoUrl(vendorData?.logo)!} 
                         alt="شعار الشركة" 
                         className="w-full h-full object-contain absolute inset-0"
                         onLoad={(e) => {
                           console.log('Logo loaded successfully:', getLogoUrl(vendorData?.logo));
                           // Hide fallback when image loads
                           const fallback = e.currentTarget.parentElement?.querySelector('.logo-fallback');
                           if (fallback) {
                             (fallback as HTMLElement).style.display = 'none';
                           }
                         }}
                         onError={(e) => {
                           console.error('Error loading logo (CORS/CSP issue):', {
                             originalPath: vendorData?.logo,
                             fullUrl: getLogoUrl(vendorData?.logo),
                             error: e
                           });
                           // Show fallback and hide image
                           const fallback = e.currentTarget.parentElement?.querySelector('.logo-fallback');
                           if (fallback) {
                             (fallback as HTMLElement).style.display = 'flex';
                           }
                           e.currentTarget.style.display = 'none';
                         }}
                         crossOrigin="anonymous"
                       />
                       {/* Fallback - shown when image fails to load */}
                       <div className="logo-fallback text-gray-400 text-center absolute inset-0 flex flex-col items-center justify-center" style={{ display: 'flex' }}>
                         <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                         </svg>
                         <span className="text-xs">شعار متوفر</span>
                         <span className="text-xs text-blue-600 mt-1 cursor-pointer" onClick={() => window.open(getLogoUrl(vendorData?.logo)!, '_blank')}>
                           عرض في تبويب جديد
                         </span>
                       </div>
                     </>
                   ) : (
                     /* No logo fallback */
                     <div className="text-gray-400 text-center">
                       <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                       </svg>
                       <span className="text-xs">لا يوجد شعار</span>
                     </div>
                   )}
                 </div>
              </div>

              {/* Upload Section */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">رفع شعار جديد</label>
                <div className="space-y-4">
                  {/* File Input */}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      الملفات المدعومة: JPG, PNG, GIF. الحد الأقصى: 5 ميجابايت
                    </p>
                  </div>

                  {/* Preview */}
                  {logoPreview && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">معاينة</label>
                      <div className="w-32 h-32 bg-gray-100 border border-gray-300 rounded-lg overflow-hidden">
                        <img 
                          src={logoPreview} 
                          alt="معاينة الشعار" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  )}

                  {/* Upload Button */}
                  {selectedLogo && (
                    <div className="flex gap-3">
                      <button
                        onClick={handleLogoUpload}
                        disabled={uploadLogoMutation.isPending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {uploadLogoMutation.isPending ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            جاري الرفع...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            رفع الشعار
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedLogo(null);
                          setLogoPreview(null);
                        }}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                      >
                        إلغاء
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vendor Information Card */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">معلومات البائع</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">اسم البائع</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.name || 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">اسم الشركة</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.companyName || 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">معرف البائع</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.vendorId || 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الحالة</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    vendorData?.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {vendorData?.isActive ? 'نشط' : 'غير نشط'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Card */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">معلومات الاتصال</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الهاتف</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.contact?.phone || 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الجوال</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.contact?.mobile || 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">البريد الإلكتروني</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.contact?.email || 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الموقع الإلكتروني</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.contact?.website || 'غير محدد'}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">الدولة</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.contact?.address?.country || 'غير محدد'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Information Card */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">معلومات الاشتراك</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">خطة الاشتراك</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {vendorData?.subscription?.plan || 'غير محدد'}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حالة الاشتراك</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    vendorData?.subscription?.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {vendorData?.subscription?.isActive ? 'نشط' : 'غير نشط'}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">تاريخ البداية</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.subscription?.startDate 
                    ? <DateDisplay value={vendorData.subscription.startDate} /> 
                    : 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">تاريخ الانتهاء</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.subscription?.endDate 
                    ? <DateDisplay value={vendorData.subscription.endDate} /> 
                    : 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">عدد المستخدمين المسموح</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.subscription?.maxUsers || 'غير محدد'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">عدد الفروع المسموح</label>
                <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {vendorData?.subscription?.maxBranches || 'غير محدد'}
                </div>
              </div>
              {vendorData?.subscription?.features && vendorData.subscription.features.length > 0 && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">المميزات المتاحة</label>
                  <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {vendorData.subscription.features.map((feature: string, index: number) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* License Information Card */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">معلومات الترخيص</h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                تعديل
              </button>
            )}
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Commercial Register - Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رقم السجل التجاري <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="commercialRegister"
                      value={licenseData.commercialRegister}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="رقم السجل التجاري"
                      required
                    />
                  ) : (
                    <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                      {licenseData.commercialRegister || 'غير محدد'}
                    </div>
                  )}
                </div>

                {/* Tourism License - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ترخيص السياحة
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="tourismLicense"
                      value={licenseData.tourismLicense}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="ترخيص السياحة"
                    />
                  ) : (
                    <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                      {licenseData.tourismLicense || 'غير محدد'}
                    </div>
                  )}
                </div>

                {/* Transportation License - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ترخيص النقل
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="transportationLicense"
                      value={licenseData.transportationLicense}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="ترخيص النقل"
                    />
                  ) : (
                    <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                      {licenseData.transportationLicense || 'غير محدد'}
                    </div>
                  )}
                </div>

                {/* Tax Number - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الرقم الضريبي
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="taxNumber"
                      value={licenseData.taxNumber}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="الرقم الضريبي"
                    />
                  ) : (
                    <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                      {licenseData.taxNumber || 'غير محدد'}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {isEditing && (
                <div className="flex justify-end space-x-4 mt-8">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-200"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    disabled={updateLicenseMutation.isPending}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                      updateLicenseMutation.isPending
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {updateLicenseMutation.isPending ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                        جاري الحفظ...
                      </div>
                    ) : (
                      'حفظ التغييرات'
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>

      
      </div>
    </div>
  );
};

export default VendorSettings; 